import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import './Textarea.css';

/**
 * Textarea Component
 * 
 * A flexible multi-line text input component with validation states and auto-resize.
 * 
 * @param {Object} props - Component props
 * @param {string} props.value - Textarea value (controlled)
 * @param {string} props.defaultValue - Default value (uncontrolled)
 * @param {function} props.onChange - Change handler
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.disabled - Disabled state
 * @param {boolean} props.readOnly - Read-only state
 * @param {boolean} props.required - Required field
 * @param {string} props.size - Textarea size: 'sm' | 'md' | 'lg'
 * @param {string} props.variant - Visual state: 'default' | 'error' | 'success' | 'warning'
 * @param {number} props.rows - Number of visible rows
 * @param {number} props.minRows - Minimum rows (for auto-resize)
 * @param {number} props.maxRows - Maximum rows (for auto-resize)
 * @param {boolean} props.resize - Allow manual resize: 'none' | 'vertical' | 'both'
 * @param {boolean} props.fullWidth - Full width textarea
 * @param {string} props.name - Textarea name attribute
 * @param {string} props.id - Textarea id attribute
 * @param {number} props.maxLength - Maximum character length
 * @param {number} props.minLength - Minimum character length
 * @param {string} props.className - Additional CSS classes
 */
const Textarea = forwardRef(({
  value,
  defaultValue,
  onChange,
  onFocus,
  onBlur,
  placeholder = '',
  disabled = false,
  readOnly = false,
  required = false,
  size = 'md',
  variant = 'default',
  rows = 4,
  resize = 'vertical',
  fullWidth = false,
  name,
  id,
  maxLength,
  minLength,
  className = '',
  ...props
}, ref) => {
  const containerClasses = [
    'textarea-container',
    `textarea-container--${size}`,
    `textarea-container--${variant}`,
    fullWidth ? 'textarea-container--full-width' : '',
    disabled ? 'textarea-container--disabled' : '',
    className
  ].filter(Boolean).join(' ');

  const textareaClasses = [
    'textarea',
    `textarea--${size}`,
    `textarea--resize-${resize}`
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClasses}>
      <textarea
        ref={ref}
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        rows={rows}
        name={name}
        id={id}
        maxLength={maxLength}
        minLength={minLength}
        className={textareaClasses}
        aria-invalid={variant === 'error' ? 'true' : undefined}
        aria-required={required ? 'true' : undefined}
        {...props}
      />
    </div>
  );
});

Textarea.displayName = 'Textarea';

Textarea.propTypes = {
  value: PropTypes.string,
  defaultValue: PropTypes.string,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  required: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  variant: PropTypes.oneOf(['default', 'error', 'success', 'warning']),
  rows: PropTypes.number,
  resize: PropTypes.oneOf(['none', 'vertical', 'both']),
  fullWidth: PropTypes.bool,
  name: PropTypes.string,
  id: PropTypes.string,
  maxLength: PropTypes.number,
  minLength: PropTypes.number,
  className: PropTypes.string,
};

export default Textarea;
